import { Button, Icon, Toast, ToastContext } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import * as React from 'react';
import { getCompanyName } from '~shared/config/getCompanyName';
import { COMMS_CHANNEL } from '~shared/constants';
import { useRequestVerification } from '~shared/queries/query-keys/verifications';
import { CommsVerificationCard } from './CommsVerificationCard';

export type Props = {
  readonly authorized: boolean;
  readonly commsChannel: string;
  readonly token: string;
};

export const CommsVerificationFail = ({ authorized, commsChannel, token }: Props) => {
  const { navigate } = useNavigation();
  const { mutateAsync: requestVerification, isPending: requestVerificationLoading } = useRequestVerification();
  const { addToast } = React.useContext(ToastContext);
  const [isVerificationSent, setIsVerificationSent] = React.useState(false);
  const formattedChannelText = COMMS_CHANNEL[commsChannel as keyof typeof COMMS_CHANNEL].toLowerCase();

  const navigateToNextRoute = () => {
    if (authorized) {
      navigate('RouteAppTabNavigator', { screen: 'RouteTabMyMeds' });
    } else {
      navigate('RouteLogin');
    }
  };

  const handleSendingVerification = async () => {
    try {
      await requestVerification({ token, commsChannel });
      addToast(<Toast>{`Verification link has been sent to your ${formattedChannelText}!`}</Toast>);
      setIsVerificationSent(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong.';
      addToast(<Toast variant="error">{`Error: ${message}`}</Toast>);
      setIsVerificationSent(false);
    }
  };

  return (
    <CommsVerificationCard
      title="Verification failed"
      text={`Your link has expired or an error occurred while attempting to verify your ${formattedChannelText}.`}
    >
      {isVerificationSent ? (
        <Icon
          name="checkcircle-small"
          type="secondary"
        />
      ) : (
        <Button
          loading={requestVerificationLoading}
          loadingLabel="Sending..."
          label="Send new verification link"
          accessibilityLabel="Send new verification link"
          onPress={handleSendingVerification}
        />
      )}
      <Button
        label={`Continue to ${getCompanyName()}`}
        accessibilityLabel={`Continue to ${getCompanyName()}`}
        onPress={navigateToNextRoute}
        type="secondary"
      />
    </CommsVerificationCard>
  );
};
